import { useCallback, useEffect, useRef, useState } from 'react';

// 뒤로가기/리마운트 직후 커서가 트리거 위에 놓여 있으면 브라우저가 hover 를 재평가하며
// 실제 마우스 조작 없이 mouseenter 를 다시 쏜다(Chrome 의 synthetic mousemove).
// 마운트 직후 이 구간의 열기 이벤트는 무시해 카드가 저절로 다시 뜨는 것을 막는다.
const OPEN_GUARD_MS = 350;

/**
 * hover 상세 카드의 표시 상태 관리 훅.
 *
 * 카드를 닫는 경로가 anchor 의 mouseleave 하나뿐이면, 창 전환(cmd+tab)·트랙패드
 * 뒤로가기 제스처·스크롤처럼 포인터가 요소 경계를 넘지 않는 상황에서 mouseleave 가
 * 발생하지 않아 카드가 영구히 남는다. 그래서 mouseleave 외에 스크롤/리사이즈/창
 * 전환(blur·visibilitychange)에서도 카드를 닫는다. 스크롤 시 위치를 따라가는 대신
 * 닫는 것은 일반적인 툴팁 UX 를 따른 것.
 */
const useHoverCard = <T extends HTMLElement>() => {
    const anchorRef = useRef<T>(null);
    const [rect, setRect] = useState<DOMRect | null>(null);
    const readyRef = useRef(false);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            readyRef.current = true;
        }, OPEN_GUARD_MS);
        return () => window.clearTimeout(timer);
    }, []);

    // 이미 떠 있으면 no-op (updater 가 prev 를 그대로 반환해 재렌더도 없다).
    const show = useCallback(() => {
        if (!readyRef.current) return;
        setRect((prev) => prev ?? anchorRef.current?.getBoundingClientRect() ?? null);
    }, []);

    const hide = useCallback(() => setRect(null), []);

    const isShown = rect !== null;
    useEffect(() => {
        if (!isShown) return;
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') hide();
        };
        // capture: true — 표 내부 스크롤 컨테이너의 스크롤은 window 로 버블링되지 않는다.
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        window.addEventListener('blur', hide);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
            window.removeEventListener('blur', hide);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [isShown, hide]);

    return {
        anchorRef,
        rect,
        // onMouseMove 도 열기 트리거로 둔다: 커서가 트리거 위에 머문 채 리마운트되면
        // 가드에 막힌 mouseenter 이후 boundary 이벤트가 다시 오지 않으므로,
        // 실제 마우스 움직임이 있을 때 카드를 띄우기 위함.
        anchorProps: { onMouseEnter: show, onMouseMove: show, onMouseLeave: hide },
    };
};

export default useHoverCard;
